import {
  faFolderOpen,
  faSave,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import swal from '@sweetalert/with-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Button from '../../components/Button';
import ContentBox from '../../components/FormArea/ContentBox';
import Dropdown from '../../components/Inputs/Dropdown';
import Input from '../../components/Inputs/Input';
import {
  definitionProblems,
  restrictToCatalog,
} from '../../logic/statisticDefinition';
import {
  failureMessage,
  loadSavedDefinitions,
  removeDefinition,
  saveDefinition,
} from './statisticApi';

const SAVED_FIELD = 'statistic-saved';
const NAME_FIELD = 'statistic-name';

function optionLabel(entry) {
  const date = entry.UpdateDate ? moment(entry.UpdateDate) : null;
  return date && date.isValid()
    ? `${entry.Name} (Stand ${date.format('DD.MM.YYYY')})`
    : entry.Name;
}

function parseDefinition(definition) {
  return typeof definition === 'string' ? JSON.parse(definition) : definition;
}

/**
 * Gespeicherte Auswertungen der Dienststelle (CARE-814). Gespeichert wird nur
 * die Definition, nie das Ergebnis - eine geladene Auswertung rechnet mit den
 * aktuellen Daten.
 */
class SavedStatistics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      entries: [],
      loadFailed: false,
      selectedGuid: null,
      loadedGuid: null,
      name: '',
      nameVersion: 0,
      saving: false,
      message: '',
    };
  }

  componentDidMount() {
    this.loadList();
  }

  loadList = () => {
    loadSavedDefinitions()
      .then((entries) => {
        this.setState({
          entries: (entries || []).filter((entry) => entry && entry.Guid),
          loadFailed: false,
        });
      })
      .catch((error) => {
        console.error('Gespeicherte Auswertungen nicht geladen:', error);
        this.setState({ loadFailed: true });
      });
  };

  entry = (guid) => this.state.entries.find((item) => item.Guid === guid);

  mayChange = (entry) => Boolean(entry && (entry.IsOwn || this.props.isAdmin));

  load = () => {
    const entry = this.entry(this.state.selectedGuid);
    if (!entry) {
      return;
    }

    let stored;
    try {
      stored = parseDefinition(entry.Definition);
    } catch (error) {
      console.error('Gespeicherte Auswertung nicht lesbar:', error);
      swal(
        'Auswertung nicht lesbar',
        `„${entry.Name}“ lässt sich nicht laden.`,
        'error',
      );
      return;
    }

    const { definition, unknownKeys } = restrictToCatalog(
      stored,
      this.props.catalog,
    );
    if (unknownKeys.length > 0) {
      swal(
        'Auswertung angepasst',
        `Diese Kennzahlen oder Dimensionen gibt es nicht mehr und wurden entfernt: ${unknownKeys.join(', ')}.`,
        'warning',
      );
    }

    // Einen fremden Namen vorzubelegen fuehrt nur zu "Name bereits vergeben".
    const editable = this.mayChange(entry);
    this.setState({
      loadedGuid: editable ? entry.Guid : null,
      name: editable ? entry.Name : '',
      nameVersion: this.state.nameVersion + 1,
      message: '',
    });
    this.props.onLoad(definition);
  };

  isUpdate = () => {
    const loaded = this.entry(this.state.loadedGuid);
    return Boolean(loaded && loaded.Name === this.state.name.trim());
  };

  save = () => {
    const name = this.state.name.trim();
    if (!name || definitionProblems(this.props.definition).length > 0) {
      return;
    }

    this.setState({ saving: true, message: '' });

    saveDefinition({
      Guid: this.isUpdate() ? this.state.loadedGuid : null,
      Name: name,
      Definition: this.props.definition,
    })
      .then((result) => {
        this.setState({ saving: false });
        if (result && result.SaveResult === 'Saved') {
          this.setState({
            selectedGuid: result.Guid || null,
            loadedGuid: result.Guid || null,
            message: `„${name}“ wurde gespeichert.`,
          });
          this.loadList();
          return;
        }
        swal(
          'Nicht gespeichert',
          (result && result.Reason) || 'Unbekannter Grund.',
          'error',
        );
      })
      .catch((error) => {
        console.error('Auswertung nicht gespeichert:', error);
        this.setState({ saving: false });
        swal(
          'Fehler',
          failureMessage(
            error && error.status,
            error && error.responseText,
            'Die Auswertung konnte nicht gespeichert werden.',
          ),
          'error',
        );
      });
  };

  remove = async () => {
    const entry = this.entry(this.state.selectedGuid);
    if (!this.mayChange(entry)) {
      return;
    }

    const confirmed = await swal({
      title: 'Auswertung löschen',
      content: (
        <p>
          <b>{entry.Name}</b> wird für alle in der Dienststelle gelöscht.
        </p>
      ),
      buttons: ['Abbrechen', 'Löschen'],
      icon: 'warning',
    });
    if (!confirmed) {
      return;
    }

    removeDefinition(entry.Guid)
      .then((result) => {
        if (result && result.SaveResult === 'Saved') {
          this.setState({
            selectedGuid: null,
            loadedGuid:
              this.state.loadedGuid === entry.Guid
                ? null
                : this.state.loadedGuid,
            message: `„${entry.Name}“ wurde gelöscht.`,
          });
          this.loadList();
          return;
        }
        swal(
          'Nicht gelöscht',
          (result && result.Reason) || 'Unbekannter Grund.',
          'error',
        );
      })
      .catch((error) => {
        console.error('Auswertung nicht geloescht:', error);
        swal(
          'Fehler',
          failureMessage(
            error && error.status,
            error && error.responseText,
            'Die Auswertung konnte nicht gelöscht werden.',
          ),
          'error',
        );
      });
  };

  renderList() {
    if (this.state.loadFailed) {
      return (
        <p className="Statistics-Error Input--fullWidth" role="alert">
          Die gespeicherten Auswertungen konnten nicht geladen werden.
        </p>
      );
    }

    if (this.state.entries.length === 0) {
      return (
        <p className="Statistics-Note Input--fullWidth">
          Es ist noch keine Auswertung gespeichert.
        </p>
      );
    }

    const selected = this.entry(this.state.selectedGuid);

    return (
      <>
        <Dropdown
          label="Gespeicherte Auswertung:"
          name={SAVED_FIELD}
          inputId={SAVED_FIELD}
          options={this.state.entries.map((entry) => ({
            value: entry.Guid,
            label: optionLabel(entry),
          }))}
          defaultValue={this.state.selectedGuid}
          placeholder="Auswertung wählen"
          getSelected={(guid) => this.setState({ selectedGuid: guid || null })}
        />
        <div className="Buttonbar">
          <Button
            icon={faFolderOpen}
            disabled={!selected}
            onClick={this.load}
          >
            Laden
          </Button>
          {this.mayChange(selected) && (
            <Button
              type="danger"
              outline
              icon={faTrashAlt}
              onClick={this.remove}
            >
              Löschen
            </Button>
          )}
        </div>
      </>
    );
  }

  render() {
    const nameMissing = this.state.name.trim() === '';
    const definitionInvalid =
      definitionProblems(this.props.definition).length > 0;

    return (
      <ContentBox title="Gespeicherte Auswertungen">
        {this.renderList()}
        <Input
          key={`${NAME_FIELD}-${this.state.nameVersion}`}
          type="text"
          label="Name der Auswertung:"
          name={NAME_FIELD}
          defaultValue={this.state.name}
          onChange={(value) => this.setState({ name: value || '' })}
        />
        <div className="Buttonbar">
          <Button
            type="primary"
            icon={faSave}
            disabled={nameMissing || definitionInvalid || this.state.saving}
            loading={this.state.saving}
            onClick={this.save}
          >
            {this.isUpdate() ? 'Änderungen speichern' : 'Speichern'}
          </Button>
        </div>
        <div className="Input--fullWidth" aria-live="polite">
          {nameMissing && (
            <p className="Statistics-Note">
              Zum Speichern bitte einen Namen eingeben.
            </p>
          )}
          {this.state.message && (
            <p className="Statistics-Note">{this.state.message}</p>
          )}
        </div>
      </ContentBox>
    );
  }
}

SavedStatistics.propTypes = {
  catalog: PropTypes.shape({
    Measures: PropTypes.arrayOf(PropTypes.shape({ Key: PropTypes.string })),
    Dimensions: PropTypes.arrayOf(PropTypes.shape({ Key: PropTypes.string })),
  }).isRequired,
  definition: PropTypes.shape({
    MeasureKeys: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isAdmin: PropTypes.bool,
  onLoad: PropTypes.func.isRequired,
};

SavedStatistics.defaultProps = {
  isAdmin: false,
};

export default SavedStatistics;
